import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { NavItem } from '@vsolv/vectors-ui/app-nav';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { SecurityService } from '@wsphere/staff/web';
import { Claim, Warranty } from '@wsphere/warranties/domain';
import { combineLatest, firstValueFrom, from, map, of, shareReplay, startWith, switchMap } from 'rxjs';
import { FeatureFlagsService, NEW_CLAIMS_FLOW_FLAG } from '../../../policy/services/feature-flags.service';
import { TenantFeature } from '../../../warranty/config/tenant-features.config';
import { TenantFeatureService } from '../../../warranty/services/tenant-feature.service';
import { CLAIM_CLOSE_CONFIG } from '../../config/claim-close.config';
import { CLAIM_WORKSPACE_CONFIG } from '../../config/claim-workspace.config';
import { ClaimItemDetailsDialog } from '../../dialogs';
import { ClaimService, IntercomLinkService } from '../../services';
import { hasOnlyFinishedJobs } from '../../utils/claim-close.util';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-page',
  templateUrl: './claim.page.html',
  standalone: false,
})
export class ClaimPage implements OnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private claimSvc: ClaimService,
    private toastSvc: ToastService,
    private securitySvc: SecurityService,
    private breakpointObserver: BreakpointObserver,
    private intercomLink: IntercomLinkService,
    private tenantFeatureSvc: TenantFeatureService
  ) {}

  private portalSvc = inject(PortalService);
  private featureFlagsSvc = inject(FeatureFlagsService);

  @ViewChild('expireClaimDialog') expireClaimDialog?: DialogComponent;
  @ViewChild('cancelClaimDialog') cancelClaimDialog?: DialogComponent;
  @ViewChild('addClaimItem') addClaimItem?: ClaimItemDetailsDialog;

  readonly claim$ = this.claimSvc.getClaim().pipe(shareReplay(1));
  readonly workspaceConfig = CLAIM_WORKSPACE_CONFIG;
  readonly closeConfig = CLAIM_CLOSE_CONFIG;

  /** Once the tenant runs the new flow, claims still in the previous flow say so. */
  readonly showsPreviousFlowNote$ = combineLatest([
    this.claim$,
    this.featureFlagsSvc.isEnabledState(NEW_CLAIMS_FLOW_FLAG),
  ]).pipe(map(([claim, newFlowOn]) => !!claim && newFlowOn === true && !Claim.Authorization.usesNewFlow(claim)));

  cancelReason = new FormControl('');

  openCreateDialog = false;

  now = new Date();

  open = Claim.Status.OPEN;
  usesNewFlow = Claim.Authorization.usesNewFlow;
  expired = Claim.Status.EXPIRED;
  cancelled = Claim.Status.CANCELLED;

  isMobile$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(state => state.matches));

  permissionKey$ = this.claim$.pipe(
    map(claim => (claim?.warranty?.distributor?.permissionKey ? [claim?.warranty?.distributor?.permissionKey] : null)),
    shareReplay(1)
  );

  canViewLifecycle$ = this.permissionKey$.pipe(
    switchMap(async permissionKey => await this.securitySvc.hasAccess('clm_ViewLifecycle', permissionKey))
  );

  canMarkClaimAsExpired$ = this.permissionKey$.pipe(
    switchMap(async permissionKey => await this.securitySvc.hasAccess('clm_ExpireClaim', permissionKey))
  );

  canCancelClaim$ = this.permissionKey$.pipe(
    switchMap(async permissionKey => await this.securitySvc.hasAccess('clm_CancelClaim', permissionKey))
  );

  canCloseClaim$ = combineLatest([this.permissionKey$, this.claim$]).pipe(
    switchMap(async ([permissionKey, claim]) => {
      let refreshClaim = null;
      if (claim) {
        refreshClaim = await this.claimSvc.get(claim.id);
      }

      return {
        canClose: await this.securitySvc.hasAccess('clm_CloseClaim', permissionKey),
        claim: refreshClaim,
      };
    }),
    map(data => data.canClose && hasOnlyFinishedJobs(data.claim))
  );

  canViewCustomerDetails$ = this.permissionKey$.pipe(
    switchMap(async permissionKey => await this.securitySvc.hasAccess('cus_ViewDetails', permissionKey))
  );

  canViewWarranty$ = this.permissionKey$.pipe(
    switchMap(async permissionKey => await this.securitySvc.hasAccess('wrt_ViewWarranty', permissionKey))
  );

  canViewDistributor$ = this.permissionKey$.pipe(
    switchMap(async permissionKey => await this.securitySvc.hasAccess('dist_ViewDistributor', permissionKey))
  );

  canViewNotes$ = this.permissionKey$.pipe(
    switchMap(async permissionKey => await this.securitySvc.hasAccess('clm_ViewNote', permissionKey)),
    shareReplay(1)
  );

  canViewAttachments$ = this.permissionKey$.pipe(
    switchMap(async permissionKey => await this.securitySvc.hasAccess('clm_ViewAttachments', permissionKey)),
    shareReplay(1)
  );

  canViewPayments$ = this.permissionKey$.pipe(
    switchMap(async permissionKey => await this.securitySvc.hasAccess('clm_ViewPayments', permissionKey)),
    shareReplay(1)
  );

  canViewAudit$ = this.permissionKey$.pipe(
    switchMap(async permissionKey => await this.securitySvc.hasAccess('clm_ViewAudit', permissionKey)),
    shareReplay(1)
  );

  // Check if user has Global Administrator access for Intercom deep linking
  canViewInIntercom$ = this.securitySvc.hasRole('*', null);

  // Check if tenant has declaration page feature enabled
  canShowDeclaration$ = this.tenantFeatureSvc.isFeatureEnabled(TenantFeature.DECLARATION_PAGE).pipe(shareReplay(1));

  assetTab$ = this.claim$.pipe(
    map(claim => (claim?.warranty?.asset ? <NavItem>{ label: 'Asset', routerLink: './asset' } : null)),
    startWith(null)
  );

  notesTab$ = combineLatest([this.claim$, this.canViewNotes$]).pipe(
    switchMap(([claim, canViewNotes]) => {
      if (!canViewNotes || !claim) return of(null);

      return from(this.claimSvc.listNotes(claim.id, { limit: 1 })).pipe(
        startWith(null),
        map(notes => {
          return <NavItem>{
            label: 'Notes',
            routerLink: './notes',
            badge: notes?.meta?.totalItems ? { text: String(notes.meta.totalItems) } : undefined,
          };
        })
      );
    }),
    startWith(null)
  );

  attachmentsTab$ = combineLatest([this.claim$, this.canViewAttachments$]).pipe(
    switchMap(([claim, canViewAttachments]) => {
      if (!canViewAttachments || !claim) return of(null);

      return from(this.claimSvc.listDocuments(claim.id, { limit: 1 })).pipe(
        startWith(null),
        map(attachments => {
          return <NavItem>{
            label: 'Attachments',
            routerLink: './attachments',
            badge: attachments?.meta?.totalItems ? { text: String(attachments.meta.totalItems) } : undefined,
          };
        })
      );
    }),
    startWith(null)
  );

  paymentTab$ = combineLatest([this.claim$, this.canViewPayments$]).pipe(
    switchMap(([claim, canViewPayments]) => {
      if (!canViewPayments || !claim) return of(null);

      return from(this.claimSvc.listPayments(claim.id, { limit: 1 })).pipe(
        startWith(null),
        map(payments => {
          return <NavItem>{
            label: 'Payments',
            routerLink: './payments',
            badge: payments?.meta?.totalItems ? { text: String(payments.meta.totalItems) } : undefined,
          };
        })
      );
    }),
    startWith(null)
  );

  auditTab$ = combineLatest([this.claim$, this.canViewAudit$]).pipe(
    switchMap(([claim, canViewAudit]) => {
      if (!canViewAudit || !claim) return of(null);

      return from(this.claimSvc.listAudit(claim.id, { limit: 1 }).catch(() => null)).pipe(
        startWith(null),
        map(audit => {
          return <NavItem>{
            label: 'Audit',
            routerLink: './audit',
            badge: audit?.meta?.totalItems ? { text: String(audit.meta.totalItems) } : undefined,
          };
        })
      );
    }),
    startWith(null)
  );

  tabs$ = combineLatest([this.assetTab$, this.notesTab$, this.attachmentsTab$, this.paymentTab$, this.auditTab$]).pipe(
    map(([assetTab, notesTab, attachmentsTab, paymentTab, auditTab]) => {
      return <NavItem[]>[
        { label: 'Overview', routerLink: './' },
        ...(assetTab ? [assetTab] : []),
        ...(paymentTab ? [paymentTab] : []),
        ...(notesTab ? [notesTab] : []),
        ...(attachmentsTab ? [attachmentsTab] : []),
        ...(auditTab ? [auditTab] : []),
      ];
    }),
    startWith([{ label: 'Overview', routerLink: './' }])
  );

  // Generate Intercom URL for customer and open in new tab
  async viewInIntercom(claim: Claim.Model) {
    await this.intercomLink.openCustomerInIntercom(claim);
  }

  claimTheme(claim: Claim.Model): ThemeColor {
    return claim?.status === Claim.Status.OPEN
      ? 'info'
      : claim?.status === Claim.Status.CLOSED
        ? 'default'
        : claim?.status === Claim.Status.CANCELLED
          ? 'warn'
          : 'danger';
  }

  warrantyTheme(claim: Claim.Model): ThemeColor {
    const warranty = claim?.warranty;
    if (!warranty) return 'default';

    return warranty.status === Warranty.Status.ACTIVATED
      ? 'success'
      : warranty.status === Warranty.Status.EXPIRED
        ? 'danger'
        : warranty.status === Warranty.Status.CANCELLED
          ? 'warn'
          : 'default';
  }

  openExpireDialog() {
    this.expireClaimDialog?.open();
  }

  async expire(claim: Claim.Model) {
    if (!claim) return;

    const expiredClaimId = await this.claimSvc.expire(claim.id).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });

      this.expireClaimDialog?.close();
    });

    if (expiredClaimId) {
      this.toastSvc.show({
        type: 'success',
        title: 'Marked Claim as Expired',
        text: `Claim has successfully been marked as expired.`,
      });

      this.expireClaimDialog?.close();
      await this.claimSvc.refreshClaim();

      this.router.onSameUrlNavigation = 'reload';

      this.router.navigate(['.'], { relativeTo: this.route, queryParams: { refresh: true } });
    }
  }

  openCancelDialog() {
    this.cancelClaimDialog?.open();
  }

  async cancel(claim: Claim.Model, reason?: string) {
    if (!claim) return;

    const cancelledClaimId = await this.claimSvc.cancel(claim.id, reason).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });

      this.cancelClaimDialog?.close();
    });

    if (cancelledClaimId) {
      this.toastSvc.show({
        type: 'success',
        title: 'Cancelled Claim',
        text: `Claim has successfully been cancelled.`,
      });

      this.cancelClaimDialog?.close();
      await this.claimSvc.refreshClaim();

      this.router.navigate(['.'], { relativeTo: this.route, queryParams: { refresh: true } });
    }
  }

  async ngOnInit(): Promise<void> {
    const claim = (await firstValueFrom(this.route.data))['claim'] as Claim.Model;
    await this.claimSvc.refreshClaim(claim);
  }
}
